import { z } from 'zod';

import { DEFAULT_RISK_FILTERS, type RiskFiltersState } from './types';

export const riskListSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(20),
  category: z.enum(['operational', 'compliance', 'strategic', 'cybersecurity']).optional(),
  subcategory: z.string().optional(),
  status: z
    .union([
      z.array(z.enum(['draft', 'pending_approval', 'open', 'in_treatment', 'accepted', 'closed'])),
      z.enum(['draft', 'pending_approval', 'open', 'in_treatment', 'accepted', 'closed']),
    ])
    .optional()
    .transform((value) => (value ? (Array.isArray(value) ? value : [value]) : undefined)),
  riskLevel: z
    .union([
      z.array(z.enum(['critical', 'high', 'medium', 'low'])),
      z.enum(['critical', 'high', 'medium', 'low']),
    ])
    .optional()
    .transform((value) => (value ? (Array.isArray(value) ? value : [value]) : undefined)),
  search: z.string().optional(),
  tags: z
    .union([z.array(z.string()), z.string()])
    .optional()
    .transform((value) => (value ? (Array.isArray(value) ? value : [value]) : undefined)),
  sortBy: z
    .enum(['created_at', 'updated_at', 'inherent_risk_score', 'title'])
    .optional()
    .default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type RiskListSearch = z.infer<typeof riskListSearchSchema>;

export function riskSearchToFilters(search: RiskListSearch): RiskFiltersState {
  return {
    category: search.category ?? null,
    subcategory: search.subcategory ?? null,
    status: search.status ?? null,
    riskLevel: search.riskLevel ?? null,
    search: search.search ?? null,
    tags: search.tags ?? null,
    ownerId: null,
    sortBy: search.sortBy,
    sortOrder: search.sortOrder,
  };
}

export function mergeRiskFiltersIntoSearch(
  currentSearch: RiskListSearch,
  filters: RiskFiltersState
): RiskListSearch {
  return {
    ...currentSearch,
    category: filters.category ?? undefined,
    subcategory: filters.subcategory ?? undefined,
    status: filters.status ?? undefined,
    riskLevel: filters.riskLevel ?? undefined,
    search: filters.search ?? undefined,
    tags: filters.tags ?? undefined,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    page: 1,
  };
}

export function clearRiskSearchFilters(currentSearch: RiskListSearch): RiskListSearch {
  return mergeRiskFiltersIntoSearch(currentSearch, DEFAULT_RISK_FILTERS);
}

export function hasActiveRiskFilters(filters: RiskFiltersState): boolean {
  return Boolean(
    filters.category ??
      filters.subcategory ??
      filters.status ??
      filters.riskLevel ??
      filters.search ??
      filters.tags?.length
  );
}
